import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ClientDataSourceService, DataSourceModel } from '../../../services/client-data-source-service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogService } from '../../utility/dialog/dialog-service';
import { ModalType } from '../../utility/dialog-template/dialog-template.component';

@Component({
  selector: 'app-client-data-source',
  templateUrl: './client-data-source.component.html',
  styleUrls: ['./client-data-source.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class ClientDataSourceComponent implements OnInit, OnChanges {
  @Input() clientId: string | null = null;
  recordId: string | null = null;
  dataSources: DataSourceModel[] = [];
  loading = false;
  error: string | null = null;
  originalCopies: { [dataSourceId: string]: DataSourceModel } = {};

  constructor(private dataSourceService: ClientDataSourceService, private dialog: DialogService,) { }

  ngOnInit(): void {
    if (this.clientId) {
      this.loadDataSources();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && this.clientId) {
      this.loadDataSources();
    }
  }

  async loadDataSources(): Promise<void> {
    if (!this.clientId) return;

    this.loading = true;
    this.error = null;

    this.dataSourceService.getDataSources(
      this.clientId,
      (sources: any[]) => {

        console.log('================ RAW API RESPONSE ================');
        console.log(JSON.stringify(sources, null, 2));

        this.dataSources = (sources ?? []).map((item: any) => {

          console.log('--- SINGLE ITEM FROM API ---');
          console.log('ITEM:', item);

          console.log('Server:', item.Server);
          console.log('Host:', item.Host);
          console.log('Database:', item.Database);

          const data: any = { ...item };
          // IMPORTANT: prevent phantom boolean fields
          const boolFields = [
            'Trusted',
            'EnableCertificateValidation',
            'EnablePassiveMode',
            'EnableSecureFtp',
            'EnablePooling',
            'Pooling',
            'ClientSessionKeepAlive',
            'UseAudience',
            'ReportEndpointInformation',
            'AutoCreateDirectories'
          ];

          for (const key of boolFields) {
            if (!(key in data)) {
              continue; 
            }
          }

          delete data.Name;
          delete data.ConnectionType;
          delete data.DataSourceId;
          delete data.LegacyEncryption;
          delete data.TablePrefix;
          delete data.EncryptionKey;
          delete data.Trusted;

          // normalization
          //if (!data.Server && data.Host) {
          //  data.Server = data.Host;
          //}

          console.log('--- FINAL UI DATA OBJECT ---');
          console.log(data);

          return {
            DataSourceId: item.DataSourceId,
            name: item.Name ?? '',
            legacyEncryption: item.LegacyEncryption === true || item.LegacyEncryption === 'True',
            tablePrefix: item.TablePrefix ?? '',
            encryptionKey: item.EncryptionKey ?? '',
            connectionType: (item.ConnectionType ?? 'File') as 'File' | 'Data',
            data,
            editing: false
          };
        });

        this.loading = false;
      },
      () => {
        this.error = 'Failed to load data sources.';
        this.loading = false;
      }
    );
  }

  onAdd(): void {
    if (!this.clientId) {
      this.error = 'Client ID is missing.';
      return;
    }

    const hasNewRow = this.dataSources.some(ds => !ds.DataSourceId && ds.editing);
    if (hasNewRow) return;

    const newDs: DataSourceModel = {
      DataSourceId: '',
      name: '',
      legacyEncryption: false,
      tablePrefix: '',
      encryptionKey: '',
      connectionType: 'File',

      //completely neutral starting state
      data: {},

      editing: true
    };

    this.dataSources.unshift(newDs);
  }
  generateGuid(): string {
    return crypto.randomUUID();
  }

  onSave(ds: DataSourceModel): void {
    if (!this.clientId) {
      this.error = 'Client ID is missing.';
      return;
    }

    if (!ds.DataSourceId) {
      ds.DataSourceId = crypto.randomUUID();
    }

    const payload: any = {
      DataSourceId: ds.DataSourceId,
      Name: ds.name ?? '',
      ConnectionType: ds.connectionType,
      LegacyEncryption: ds.legacyEncryption ? 'True' : 'False',
      TablePrefix: ds.tablePrefix ?? '',
      ...ds.data
    };

    //normalize connection fields (CRITICAL FOR BACKEND ENCRYPTION)
    if (payload.Server && !payload.Host) {
      payload.Host = payload.Server;
    }

    if (payload.User && !payload.Username) {
      payload.Username = payload.User;
    }

    //ensure DataSourceType always exists (Data connections only)
    if (payload.ConnectionType === 'Data') {
      payload.DataSourceType = payload.DataSourceType ?? '';
    }

    //normalize boolean dictionary fields (backend expects string)
    const boolFields = [
      'Trusted',
      'EnableCertificateValidation',
      'EnablePassiveMode',
      'EnableSecureFtp',
      'EnablePooling',
      'Pooling',
      'ClientSessionKeepAlive',
      'UseAudience',
      'ReportEndpointInformation',
      'AutoCreateDirectories'
    ];

    for (const key of boolFields) {
      if (payload[key] !== undefined) {
        payload[key] =
          payload[key] === true ||
            payload[key] === 'True' ||
            payload[key] === 'true'
            ? 'True'
            : 'False';
      }
    }

    //backend safety
    payload.EncryptionKey = payload.EncryptionKey ?? '';
    payload.TablePrefix = payload.TablePrefix ?? '';

    this.dataSourceService.saveDataSource(
      this.clientId,
      ds.DataSourceId,
      payload,
      () => {
        ds.editing = false;
        this.loadDataSources();
      },
      () => {
        this.error = 'Failed to save data source.';
      }
    );
  }

  onDelete(dsId: string): void {
    if (!this.clientId) {
      this.error = 'Client ID is missing.';
      return;
    }

    this.dialog.openModal(
      'Confirm Deletion',
      'Are you sure you want to delete this data source?',
      ModalType.QUESTION,
      () => {
        this.dataSourceService.deleteDataSource(
          this.clientId!,
          dsId,
          () => {
            this.loadDataSources();
          },
          () => {
            this.error = 'Failed to delete data source.';
          }
        );
      }
    );
  }

  onEdit(ds: DataSourceModel): void {
    this.dataSources.forEach(d => {
      if (d !== ds && d.editing) {
        this.onCancel(d);
      }
    });

    ds.editing = true;

    this.originalCopies[ds.DataSourceId || 'new'] = {
      ...ds,
      data: { ...ds.data }
    };
  }

  onCancel(ds: DataSourceModel): void {
    const dataSourceId = ds.DataSourceId || 'new';

    if (!ds.DataSourceId) {
      this.dataSources = this.dataSources.filter(d => d !== ds);
      return;
    }
    const original = this.originalCopies[dataSourceId];

    if (original) {
      ds.name = original.name;
      ds.legacyEncryption = original.legacyEncryption;
      ds.tablePrefix = original.tablePrefix;
      ds.encryptionKey = original.encryptionKey;
      ds.connectionType = original.connectionType;
      ds.data = { ...original.data };

      delete this.originalCopies[dataSourceId];
    }

    ds.editing = false;
  }

  get isEditingAnyRow(): boolean {
    return this.dataSources.some(ds => ds.editing);
  }

}
